/**
 * Working out who can take an order.
 *
 * WHY THIS IS NOT LEFT TO THE DISPATCHER
 * -------------------------------------
 * A rider on cash-on-delivery is carrying the shop's money, and their limit is
 * how much the business will have walking around. Whether they can take the
 * next cash order is a subtraction, done under pressure, several times an
 * hour — and getting it wrong means the server refuses the assignment after
 * the dispatcher has already told the rider to go.
 *
 * So the screen does the arithmetic and says plainly who is available for this
 * particular order, rather than listing everyone and letting the API say no.
 */
import { round2, minutesSince } from '@foodrun/shared';

/** What the customer still owes — the cash the rider will carry back. */
export function cashOnDelivery(order) {
  return round2(
    Math.max(0, (Number(order?.total_amount) || 0) - (Number(order?.paid_amount) || 0))
  );
}

/** Room left under a rider's limit. */
export function headroom(rider) {
  return round2((Number(rider?.cod_limit) || 0) - (Number(rider?.cod_balance) || 0));
}

/**
 * Can this rider take this order, and if not, why?
 *
 * The reason matters as much as the answer — "over their cash limit" is
 * something a dispatcher can act on; a greyed-out name is not.
 */
export function canTake(rider, order) {
  if (rider?.is_blocked) {
    return { ok: false, reason: rider.block_reason ? `Blocked: ${rider.block_reason}` : 'Blocked' };
  }
  if (!rider?.is_active) return { ok: false, reason: 'Not active' };

  const owed = cashOnDelivery(order);
  if (owed > 0 && owed > headroom(rider)) {
    return {
      ok: false,
      reason: `Carrying Rs. ${rider.cod_balance} — needs to hand cash in first`,
    };
  }

  return { ok: true };
}

/**
 * Riders, best first.
 *
 * Whoever can take it and is carrying the least, because spreading the cash
 * around is what keeps anyone from hitting their limit mid-evening. Riders who
 * cannot take it stay on the list, with the reason — a name that vanishes is a
 * question a dispatcher then has to go and ask.
 */
export function rankForOrder(riders, order) {
  return [...riders]
    .map((rider) => ({ rider, ...canTake(rider, order) }))
    .sort((a, b) => {
      if (a.ok !== b.ok) return a.ok ? -1 : 1;
      // Fewest orders already out, then least cash held.
      const load = (a.rider.orders_out ?? 0) - (b.rider.orders_out ?? 0);
      if (load !== 0) return load;
      return (a.rider.cod_balance ?? 0) - (b.rider.cod_balance ?? 0);
    });
}

/**
 * How long an order has been sitting, in whole minutes.
 *
 * The stored timestamp is UTC. Read as local it comes out hours in the future,
 * and every order on the board then shows as having just arrived.
 */
export function waitingMinutes(order, now = Date.now()) {
  return minutesSince(order?.created_at, now);
}
